/**
 * Navigation definition — two-context IA per NAVIGATION_MAP.
 *
 * SYSTEM context: shown when the user is at the system level (not inside a workspace).
 * WORKSPACE context: shown when the user has entered a specific workspace (AM POV).
 *
 * Icons: NAV_ICONS from @teivor/shared-ui (MingCute line/fill pairs only — no Lucide).
 * Role gating: UX-only; backend @Roles guards are authoritative.
 */

import { NAV_ICONS } from '@teivor/shared-ui';
import type { NavGlyph } from '@teivor/shared-ui';

// Re-export NavGlyph so consumers (AppLayout) can type icon props.
export type { NavGlyph };

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface NavItem {
  /** React Router path */
  to: string;
  /** i18n key */
  labelKey: string;
  /**
   * JWT roles that may SEE this item (UX only; backend @Roles is authoritative).
   * Undefined = visible to anyone who can see the parent node. MUST mirror the
   * route guard, or a role sees a menu entry its route then blocks.
   */
  roles?: string[];
}

export interface NavNode {
  id: string;
  /** i18n key for the group label */
  labelKey: string;
  /** For active-detection: longest-prefix match */
  pathPrefix: string;
  /** MingCute line/fill pair from NAV_ICONS */
  glyph: NavGlyph;
  /** Primary navigation items (shown in subtab / command palette) */
  items: NavItem[];
  /**
   * JWT roles that may SEE this node. UX gating only.
   * Undefined = visible to all authenticated roles.
   */
  roles?: string[];
}

// ─── SYSTEM context nav tree ───────────────────────────────────────────────────
// Matches NAVIGATION_MAP §"System context (staff shell — admin; manager where noted)"

export const SYSTEM_NAV: NavNode[] = [
  {
    id: 'workspaces',
    labelKey: 'nav.workspaces',
    pathPrefix: '/shell/workspaces',
    glyph: NAV_ICONS.workspaces,
    items: [
      { to: '/shell/workspaces', labelKey: 'nav.workspaces' },
      { to: '/shell/workspaces/new', labelKey: 'nav.workspaces_new' },
    ],
    // All staff roles can reach the picker (their own filter applies server-side).
  },
  {
    id: 'leadPool',
    labelKey: 'nav.lead_pool',
    pathPrefix: '/shell/lead-pool',
    glyph: NAV_ICONS.leadPool,
    items: [
      // Per-item roles mirror the route guards in routes/index.tsx so managers/AMs
      // never see a Lead Pool entry that the route then blocks.
      { to: '/shell/lead-pool', labelKey: 'nav.lead_pool_browser', roles: ['admin', 'manager'] },
      { to: '/shell/lead-pool/columns', labelKey: 'nav.lead_pool_columns', roles: ['admin'] },
      { to: '/shell/lead-pool/import', labelKey: 'nav.lead_pool_import', roles: ['admin', 'am'] },
      { to: '/shell/lead-pool/history', labelKey: 'nav.lead_pool_history' },
      { to: '/shell/lead-pool/settings', labelKey: 'nav.lead_pool_settings', roles: ['admin'] },
    ],
    roles: ['admin', 'manager', 'am'],
  },
  {
    // Lead Board — where the leads in the pool above actually came from. Sits directly under
    // Lead Pool because it is the same data one step earlier: scrape → enrich → import.
    id: 'leadBoard',
    labelKey: 'nav.lead_board',
    pathPrefix: '/shell/lead-board',
    glyph: NAV_ICONS.leadPool,
    items: [
      { to: '/shell/lead-board', labelKey: 'nav.lead_board_overview' },
      { to: '/shell/lead-board/batches', labelKey: 'nav.lead_board_batches' },
      { to: '/shell/lead-board/changelog', labelKey: 'nav.lead_board_changelog' },
    ],
    roles: ['admin', 'manager', 'am'],
  },
  {
    // Logs — replaced the non-functional System Settings directory. Surfaces the two
    // real system logs: notification/transactional delivery + the append-only audit log.
    id: 'logs',
    labelKey: 'nav.logs',
    pathPrefix: '/ntf/delivery-log',
    glyph: NAV_ICONS.systemSettings,
    items: [
      { to: '/ntf/delivery-log', labelKey: 'nav.delivery_log' },
      { to: '/moa/audit-log', labelKey: 'nav.audit_log' },
    ],
    roles: ['admin', 'manager'],
  },
  {
    // Org-level Attention page was removed in the declutter (attention is handled in
    // the inbox). AM-unassign approvals — the one remaining system-level task — stays.
    id: 'unassignApprovals',
    labelKey: 'nav.unassign_approvals',
    pathPrefix: '/iam/attention/unassign-approvals',
    glyph: NAV_ICONS.attention,
    items: [
      { to: '/iam/attention/unassign-approvals', labelKey: 'nav.unassign_approvals' },
    ],
    roles: ['admin', 'manager'],
  },
  {
    id: 'compliance',
    labelKey: 'nav.compliance',
    pathPrefix: '/grc',
    glyph: NAV_ICONS.compliance,
    // Audit log moved to the Logs section in the declutter. DSAR + Suppression +
    // Retention are the live GDPR functions; Exports is the compliance export queue.
    items: [
      { to: '/grc/dsar', labelKey: 'nav.dsar' },
      { to: '/grc/suppression', labelKey: 'nav.suppression' },
      { to: '/grc/retention', labelKey: 'nav.retention' },
      { to: '/shell/compliance/exports', labelKey: 'nav.exports' },
    ],
    roles: ['admin', 'manager', 'compliance_reviewer'],
  },
  {
    // AI configuration consumed by the live reply/send pipeline. (Cost + Observability
    // pages were removed in the declutter — low value; Grafana covers ops metrics.)
    id: 'aiSettings',
    labelKey: 'nav.ai_settings',
    pathPrefix: '/aig/providers',
    glyph: NAV_ICONS.agents,
    items: [
      { to: '/aig/providers', labelKey: 'nav.aig_providers' },
      { to: '/aig/confidence-autonomy', labelKey: 'nav.aig_confidence' },
    ],
    roles: ['admin', 'manager'],
  },
  {
    id: 'aiSafety',
    labelKey: 'nav.ai_safety',
    pathPrefix: '/aig/safety',
    glyph: NAV_ICONS.compliance,
    items: [
      { to: '/aig/safety', labelKey: 'nav.aig_incidents' },
      { to: '/aig/safety/patterns', labelKey: 'nav.aig_patterns' },
    ],
    roles: ['admin', 'manager'],
  },
  {
    id: 'administration',
    labelKey: 'nav.admin',
    pathPrefix: '/admin',
    glyph: NAV_ICONS.account,
    items: [
      { to: '/admin/users', labelKey: 'nav.users' },
      { to: '/admin/workspaces', labelKey: 'nav.workspaces' },
      { to: '/admin/integrations', labelKey: 'nav.integrations' },
    ],
    roles: ['admin'],
  },
  // Personal settings (My account) is intentionally NOT in the sidebar — it is reached
  // from the topbar user-avatar menu, and owns an in-page tab bar for its sub-sections.
];

// ─── WORKSPACE context nav tree ────────────────────────────────────────────────
// Matches NAVIGATION_MAP §"Workspace context (AM point of view)"

export const WORKSPACE_NAV: NavNode[] = [
  {
    id: 'overview',
    labelKey: 'nav.overview',
    pathPrefix: '/ws/overview',
    glyph: NAV_ICONS.overview,
    items: [{ to: '/ws/overview', labelKey: 'nav.overview' }],
  },
  {
    id: 'liveBoard',
    labelKey: 'nav.live_board',
    pathPrefix: '/ws/live',
    glyph: NAV_ICONS.orgAnalytics,
    items: [{ to: '/ws/live', labelKey: 'nav.live_board' }],
  },
  {
    id: 'inbox',
    labelKey: 'nav.inbox',
    pathPrefix: '/ws/inbox',
    glyph: NAV_ICONS.inbox,
    items: [{ to: '/ws/inbox', labelKey: 'nav.inbox' }],
  },
  {
    id: 'attentionQueue',
    labelKey: 'nav.attention_queue',
    pathPrefix: '/ws/attention',
    glyph: NAV_ICONS.attentionQueue,
    items: [
      { to: '/ws/attention', labelKey: 'nav.attention_queue' },
      { to: '/ws/bookings', labelKey: 'nav.bookings' },
    ],
  },
  {
    id: 'campaigns',
    labelKey: 'nav.campaigns',
    pathPrefix: '/ws/campaigns',
    glyph: NAV_ICONS.campaigns,
    items: [
      { to: '/ws/campaigns', labelKey: 'nav.campaigns_list' },
      { to: '/ws/campaigns/active', labelKey: 'nav.campaigns_active' },
      { to: '/ws/sequences', labelKey: 'nav.sequences' },
    ],
  },
  {
    // Confirm-before-send. Only meaningful when the workspace toggle is on, but the section stays
    // visible either way so an operator can find the queue that is holding their campaign.
    id: 'sending',
    labelKey: 'nav.sending',
    pathPrefix: '/ws/sending',
    glyph: NAV_ICONS.approvals,
    items: [
      { to: '/ws/sending/review', labelKey: 'nav.send_review' },
      { to: '/ws/sending/outbound', labelKey: 'nav.send_outbound' },
    ],
  },
  {
    id: 'leadsIcps',
    labelKey: 'nav.leads_icps',
    pathPrefix: '/ws/leads',
    glyph: NAV_ICONS.leadsIcps,
    items: [{ to: '/ws/leads', labelKey: 'nav.leads_icps' }],
  },
  {
    id: 'emailsDomains',
    labelKey: 'nav.emails_domains',
    pathPrefix: '/ws/inboxes',
    glyph: NAV_ICONS.emailsDomains,
    items: [{ to: '/ws/inboxes', labelKey: 'nav.inboxes_domains' }],
  },
  {
    id: 'agents',
    labelKey: 'nav.agents',
    pathPrefix: '/ws/agents',
    glyph: NAV_ICONS.agents,
    // Scripts (conversation playbooks) + Documents (attachment library) live as subtabs under Agents.
    items: [
      { to: '/ws/agents', labelKey: 'nav.agents' },
      { to: '/ws/scripts', labelKey: 'nav.scripts' },
      { to: '/ws/documents', labelKey: 'nav.documents' },
    ],
  },
  {
    id: 'knowledgeBase',
    labelKey: 'nav.knowledge_base',
    pathPrefix: '/ws/kb',
    glyph: NAV_ICONS.knowledgeBase,
    items: [{ to: '/ws/kb', labelKey: 'nav.knowledge_base' }],
  },
  {
    id: 'clientChat',
    labelKey: 'nav.client_chat',
    pathPrefix: '/ws/chat',
    glyph: NAV_ICONS.clientChat,
    items: [{ to: '/ws/chat', labelKey: 'nav.client_chat' }],
  },
  {
    id: 'workspaceSettings',
    labelKey: 'nav.workspace_settings',
    pathPrefix: '/ws/settings',
    glyph: NAV_ICONS.workspaceSettings,
    items: [
      { to: '/ws/settings/languages', labelKey: 'nav.ws_languages' },
      { to: '/ws/settings/autonomy', labelKey: 'nav.ws_autonomy' },
      { to: '/ws/settings/cooldown', labelKey: 'nav.ws_cooldown' },
      { to: '/ws/settings/limits', labelKey: 'nav.ws_limits' },
      { to: '/ws/settings/pacing', labelKey: 'nav.ws_pacing' },
      { to: '/ws/settings/slas', labelKey: 'nav.ws_slas' },
      { to: '/ws/settings/telegram', labelKey: 'nav.ws_telegram' },
      { to: '/ws/settings/account-managers', labelKey: 'nav.ws_ams' },
      { to: '/ws/settings/users', labelKey: 'nav.ws_client_users' },
    ],
  },
  // Personal settings (My account) is reached from the topbar user-avatar menu — see
  // the note in SYSTEM_NAV. It is deliberately absent from both sidebars.
];

// ─── Backwards-compat types + aliases (AppLayout imports these) ───────────────

/**
 * @deprecated Use NavNode + SYSTEM_NAV / WORKSPACE_NAV.
 * Kept for the legacy Breadcrumbs that still use NavGroup.
 */
export type NavGroup = NavNode & { icon: React.FC<React.SVGProps<SVGSVGElement>> };

import type * as React from 'react';

/** Build a legacy NavGroup from a NavNode so consumers of the old API still work. */
function toNavGroup(node: NavNode): NavGroup {
  return {
    ...node,
    // Supply `.icon` as the line variant so legacy code gets a working icon component.
    icon: node.glyph.line as React.FC<React.SVGProps<SVGSVGElement>>,
  };
}

/** All system nav items as NavGroups — for the legacy Breadcrumbs. */
export const NAV_GROUPS: NavGroup[] = SYSTEM_NAV.map(toNavGroup);

/** Active system nav group for a pathname. Used by legacy Breadcrumbs. */
export function detectActiveGroup(pathname: string): NavGroup {
  if (pathname === '/') return NAV_GROUPS[0]!;
  const matches = NAV_GROUPS.filter(
    (g) => g.id !== 'workspaces' && pathname.startsWith(g.pathPrefix),
  ).sort((a, b) => b.pathPrefix.length - a.pathPrefix.length);
  return matches[0] ?? NAV_GROUPS[0]!;
}

/** A node may SEE the role (section-level gate). */
function nodeVisible(node: NavNode, role: string | undefined): boolean {
  return !node.roles || (role !== undefined && node.roles.includes(role));
}

/** Drop the items a role may not open, so sub-nav + command palette never show a
 *  link the route would block. Mirrors the per-route @Roles. */
function gateItems<T extends NavNode>(node: T, role: string | undefined): T {
  if (!node.items.some((i) => i.roles)) return node;
  return {
    ...node,
    items: node.items.filter((i) => !i.roles || (role !== undefined && i.roles.includes(role))),
  } as T;
}

/** Visible system nav groups for a role (node + per-item gating). */
export function visibleNavGroups(role: string | undefined): NavGroup[] {
  return NAV_GROUPS.filter((g) => nodeVisible(g, role)).map((g) => gateItems(g, role));
}

/** Visible system nav NODES for a role (node + per-item gating). */
export function visibleSystemNav(role: string | undefined): NavNode[] {
  return SYSTEM_NAV.filter((n) => nodeVisible(n, role)).map((n) => gateItems(n, role));
}

/** Visible workspace nav NODES for a role (node + per-item gating). */
export function visibleWorkspaceNav(role: string | undefined): NavNode[] {
  return WORKSPACE_NAV.filter((n) => nodeVisible(n, role)).map((n) => gateItems(n, role));
}

/** Active workspace nav node for a pathname. */
export function detectActiveWorkspaceNode(pathname: string): NavNode | undefined {
  return [...WORKSPACE_NAV]
    .filter((n) => pathname.startsWith(n.pathPrefix))
    .sort((a, b) => b.pathPrefix.length - a.pathPrefix.length)[0];
}

/**
 * Active section for the section column (subtab menu). A section matches when the
 * pathname matches its `pathPrefix` OR any of its item paths — a section's items can
 * live under a different prefix (e.g. Campaigns owns /ws/sequences + /ws/templates;
 * Compliance owns /moa/audit-log + /shell/compliance/exports). Without item matching,
 * landing on a non-main subtab found no section and the column vanished.
 *
 * Matching is boundary-aware (so /ws/inbox does not match /ws/inboxes) and returns the
 * most specific (longest matching base) section.
 */
export function findActiveSection(
  nodes: NavNode[],
  pathname: string,
): NavNode | undefined {
  const matches = (base: string) => pathname === base || pathname.startsWith(`${base}/`);
  let best: NavNode | undefined;
  let bestLen = -1;
  for (const node of nodes) {
    for (const base of [node.pathPrefix, ...node.items.map((it) => it.to)]) {
      if (matches(base) && base.length > bestLen) {
        best = node;
        bestLen = base.length;
      }
    }
  }
  return best;
}
